<template>
  <div class="bg-gray-50 min-h-full">
    <div class="space-y-4">
      <!-- 搜索和筛选区域 -->
      <div class="bg-white rounded-lg border border-gray-200 shadow-sm">
        <div class="p-4">
          <!-- 第一行：筛选器 -->
          <div class="flex flex-col sm:flex-row gap-2 mb-4">
            <!-- 搜索框 -->
            <div class="flex-1 min-w-0">
              <div class="relative">
                <input
                  v-model="searchQuery"
                  type="text"
                  placeholder="搜索订单号、PayPal号..."
                  class="w-full h-10 pl-9 pr-3 text-sm bg-gray-50/50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:bg-white transition-all duration-200 placeholder-gray-400"
                  @keyup.enter="handleSearch"
                >
                <svg class="absolute left-2.5 top-2.5 h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>

            <!-- 时间筛选按钮 -->
            <button
              @click="showDateFilterModal = true"
              :class="[
                'w-full sm:w-44 inline-flex items-center justify-center h-10 px-4 text-sm font-medium rounded-lg focus:ring-2 focus:ring-blue-500/20 transition-all duration-200 whitespace-nowrap',
                selectedDateField ? 'text-white bg-blue-600 hover:bg-blue-700 border border-blue-600' : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 hover:border-gray-400'
              ]"
            >
              <svg class="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              时间筛选
              <span v-if="selectedDateField" class="ml-1.5 flex h-2 w-2">
                <span class="animate-ping absolute inline-flex h-2 w-2 rounded-full bg-white opacity-75"></span>
                <span class="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
              </span>
            </button>

            <!-- 服务商筛选 -->
            <div class="w-full sm:w-44">
              <SearchableSelect
                v-model="selectedServiceProvider"
                :options="serviceProviderOptions"
                placeholder="全部服务商"
                label-key="name"
                value-key="id"
                @update:modelValue="handleSearch"
              />
            </div>

            <!-- 返款形式筛选 -->
            <div class="w-full sm:w-44">
              <SearchableSelect
                v-model="selectedRefundType"
                :options="refundTypeOptions"
                placeholder="全部返款形式"
                label-key="name"
                value-key="id"
                @update:modelValue="handleSearch"
              />
            </div>

            <!-- 搜索按钮 -->
            <button
              @click="handleSearch"
              :disabled="loading"
              class="inline-flex items-center justify-center h-10 px-4 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 hover:border-blue-300 focus:ring-2 focus:ring-blue-500/20 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            >
              <svg class="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              搜索订单
            </button>

            <!-- 添加订单按钮 -->
            <button
              @click="handleAddOrder"
              class="inline-flex items-center justify-center h-10 px-4 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg hover:from-blue-700 hover:to-blue-800 focus:ring-2 focus:ring-blue-500/20 focus:ring-offset-2 shadow-sm hover:shadow-md transform hover:scale-105 transition-all duration-200 whitespace-nowrap"
            >
              <svg class="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
              </svg>
              添加订单
            </button>
          </div>

          <!-- 第二行：管理按钮 -->
          <div class="flex flex-col sm:flex-row gap-4">
            <!-- 管理和导入导出按钮 -->
            <div class="grid grid-cols-2 gap-2 sm:flex sm:gap-2">
              <button
                @click="showFinancialSummaryModal = true"
                class="inline-flex items-center justify-center h-10 px-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 focus:ring-2 focus:ring-blue-500/20 transition-all duration-200"
              >
                <svg class="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
                财务统计
              </button>
              <button
                @click="handleFieldConfig"
                class="inline-flex items-center justify-center h-10 px-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 focus:ring-2 focus:ring-blue-500/20 transition-all duration-200"
              >
                <svg class="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                </svg>
                字段配置
              </button>
              <button
                @click="handleImport"
                class="inline-flex items-center justify-center h-10 px-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 focus:ring-2 focus:ring-blue-500/20 transition-all duration-200"
              >
                <svg class="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l4 4m0 0l-4 4m4-4H4" />
                </svg>
                导入订单
              </button>
              <button
                v-if="canExportOrders"
                @click="handleExport"
                class="inline-flex items-center justify-center h-10 px-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 focus:ring-2 focus:ring-blue-500/20 transition-all duration-200"
              >
                <svg class="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                导出订单
              </button>
            </div>
          </div>
        </div>
      </div>


      <!-- 订单列表 -->
      <div class="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <div v-if="loading" class="flex items-center justify-center py-20">
          <div class="text-center">
            <svg class="animate-spin h-8 w-8 text-blue-600 mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <p class="text-gray-500">加载中...</p>
          </div>
        </div>

        <!-- 空状态 -->
        <div v-else-if="orders.length === 0" class="text-center py-20">
          <svg class="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <h3 class="text-lg font-medium text-gray-900 mb-2">
            {{ apiError ? '加载失败' : (searchQuery || selectedServiceProvider || selectedRefundType ? '无匹配结果' : '暂无订单') }}
          </h3>
          <p class="text-gray-500 mb-6">
            {{ apiError ? '请检查网络连接或稍后重试' : (searchQuery || selectedServiceProvider || selectedRefundType ? '尝试调整搜索条件或筛选条件' : '开始添加您的第一个订单') }}
          </p>
          <button
            v-if="!apiError"
            @click="handleAddOrder"
            class="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500/20 focus:ring-offset-2 transition-all duration-200"
          >
            <svg class="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
            </svg>
            添加订单
          </button>
        </div>

        <!-- 批量操作栏 -->
        <div v-if="selectedOrders.length > 0" class="bg-blue-50 px-4 sm:px-6 py-3 border-b border-gray-200">
          <div class="flex items-center justify-between">
            <div class="flex items-center space-x-3">
              <span class="text-sm text-gray-700">已选择 {{ selectedOrders.length }} 个订单</span>
              <button
                type="button"
                @click="selectedOrders = []"
                class="text-sm text-gray-500 hover:text-gray-700"
              >
                取消选择
              </button>
            </div>
            <div class="flex items-center space-x-2">
              <button
                v-if="canDeleteOrders"
                type="button"
                @click="handleBulkDelete"
                :disabled="bulkDeleting"
                class="inline-flex items-center px-3 py-1 border border-red-300 shadow-sm text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg v-if="bulkDeleting" class="animate-spin -ml-1 mr-2 h-4 w-4 text-red-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {{ bulkDeleting ? '删除中...' : '批量删除' }}
              </button>
            </div>
          </div>
        </div>

        <!-- 订单数据内容（桌面端表格视图） -->
        <div v-if="!loading && orders.length > 0" class="hidden sm:block relative">
          <div class="flex overflow-hidden" ref="tableContainer">
            <!-- 左侧固定列：复选框 -->
            <div class="flex-shrink-0 bg-white" ref="leftTableContainer">
              <table class="w-[60px]">
                <thead class="bg-gray-50">
                  <tr>
                    <th class="pl-6 pr-2 py-3 text-left w-[60px] h-[52px]">
                      <input
                        type="checkbox"
                        :checked="isAllSelected"
                        :indeterminate="isIndeterminate"
                        @change="toggleSelectAll"
                        class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                    </th>
                  </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                  <tr v-for="order in orders" :key="`checkbox-${order.id}`" class="hover:bg-gray-50 group">
                    <td class="pl-6 pr-2 py-4 whitespace-nowrap w-[60px] group-hover:bg-gray-50 transition-colors">
                      <input
                        type="checkbox"
                        :value="order.id"
                        v-model="selectedOrders"
                        class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- 中间滚动区域：数据列（排除操作列） -->
            <div class="flex-1 overflow-x-auto table-container" ref="middleTableContainer">
              <table class="w-full table-auto min-w-max">
                <thead class="bg-gray-50">
                  <tr>
                    <th 
                      v-for="fieldConfig in visibleFieldConfigs" 
                      :key="`header-${fieldConfig.field_name}`"
                      class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider h-[52px]"
                      :style="{ width: fieldConfig.column_width ? `${fieldConfig.column_width}px` : 'auto' }"
                    >
                      {{ fieldConfig.field_label }}
                    </th>
                  </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                  <tr v-for="order in orders" :key="`data-${order.id}`" class="hover:bg-gray-50 group">
                    <td 
                      v-for="fieldConfig in visibleFieldConfigs" 
                      :key="`data-${order.id}-${fieldConfig.field_name}`"
                      class="px-6 py-4 text-sm text-gray-500"
                      :style="{ 
                        width: fieldConfig.column_width ? `${fieldConfig.column_width}px` : 'auto',
                        maxWidth: fieldConfig.column_width ? `${fieldConfig.column_width}px` : 'auto'
                      }"
                    >
                    <!-- 订单编号字段（优先检查，重复的标记为红色） -->
                    <template v-if="fieldConfig.field_name === 'order_number'">
                      <div 
                        class="truncate font-medium" 
                        :class="duplicateOrderNumbers.includes(order.order_number) ? 'text-red-600' : 'text-gray-900'"
                        :title="`${order.order_number || '-'} ${duplicateOrderNumbers.includes(order.order_number) ? '(重复)' : ''}`"
                      >
                        {{ order.order_number || '-' }}
                        <span v-if="duplicateOrderNumbers.includes(order.order_number)" class="text-xs ml-1">(重复)</span>
                      </div>
                    </template>
                    
                    <!-- 图片字段 -->
                    <template v-else-if="['order_screenshot', 'paypal_refund_screenshot', 'review_screenshot'].includes(fieldConfig.field_name)">
                      <div v-if="order[fieldConfig.field_name + '_url']" class="flex items-center">
                        <img 
                          :src="order[fieldConfig.field_name + '_url']" 
                          :alt="fieldConfig.field_label"
                          class="h-10 w-10 object-cover rounded cursor-pointer hover:opacity-75 transition-opacity"
                          @click="showImagePreview(order[fieldConfig.field_name + '_url'])"
                        />
                      </div>
                      <span v-else class="text-gray-400">-</span>
                    </template>
                    
                    <!-- 评论链接字段 -->
                    <template v-else-if="fieldConfig.field_name === 'review_link'">
                      <a 
                        v-if="order.review_link" 
                        :href="order.review_link" 
                        target="_blank" 
                        class="text-blue-600 hover:text-blue-800 hover:underline text-xs flex items-center"
                      >
                        <svg class="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                        查看
                      </a>
                      <span v-else class="text-gray-400">-</span>
                    </template>
                    
                    <!-- 状态字段 -->
                    <template v-else-if="fieldConfig.field_name === 'status'">
                      <span :class="getStatusClass(order.status)" class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium">
                        {{ order.status_display }}
                      </span>
                    </template>
                    
                    <!-- 返款形式字段 -->
                    <template v-else-if="fieldConfig.field_name === 'refund_type'">
                      <div class="truncate" :title="order.refund_type_display || '-'">
                        {{ order.refund_type_display || '-' }}
                      </div>
                    </template>
                    
                    <!-- PayPal链接字段 -->
                    <template v-else-if="fieldConfig.field_name === 'paypal_link'">
                      <a 
                        v-if="order.paypal_link" 
                        :href="order.paypal_link" 
                        target="_blank" 
                        class="text-blue-600 hover:text-blue-800 hover:underline text-xs flex items-center"
                      >
                        <svg class="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                        查看
                      </a>
                      <span v-else class="text-gray-400">-</span>
                    </template>
                    
                    <!-- 人民币金额字段 -->
                    <template v-else-if="['rmb_total_amount', 'commission', 'other_amount'].includes(fieldConfig.field_name)">
                      <div class="truncate font-medium text-gray-900" :title="order[fieldConfig.field_name] ? '¥' + formatAmount(order[fieldConfig.field_name]) : '-'">
                        {{ order[fieldConfig.field_name] ? '¥' + formatAmount(order[fieldConfig.field_name]) : '-' }}
                      </div>
                    </template>
                    
                    <!-- 汇损字段（无货币符号） -->
                    <template v-else-if="fieldConfig.field_name === 'exchange_loss'">
                      <div class="truncate font-medium text-gray-900" :title="order[fieldConfig.field_name] || '-'">
                        {{ order[fieldConfig.field_name] || '-' }}
                      </div>
                    </template>
                    
                    <!-- 美元金额字段 -->
                    <template v-else-if="['paypal_refund_amount', 'principal', 'principal_plus_pp'].includes(fieldConfig.field_name)">
                      <div class="truncate font-medium text-gray-900" :title="order[fieldConfig.field_name] ? '$' + formatAmount(order[fieldConfig.field_name]) : '-'">
                        {{ order[fieldConfig.field_name] ? '$' + formatAmount(order[fieldConfig.field_name]) : '-' }}
                      </div>
                    </template>
                    
                    <!-- 汇率字段 -->
                    <template v-else-if="fieldConfig.field_name === 'exchange_rate'">
                      <div class="truncate text-gray-900" :title="order.exchange_rate || '-'">
                        {{ order.exchange_rate || '-' }}
                      </div>
                    </template>
                    
                    <!-- 备注字段 -->
                    <template v-else-if="fieldConfig.field_name === 'notes'">
                      <div class="truncate" :title="order.notes">
                        {{ order.notes || '-' }}
                      </div>
                    </template>
                    
                    <!-- 操作字段 -->
                    <template v-else-if="fieldConfig.field_name === 'actions'">
                      <div class="flex items-center space-x-3">
                        <button
                          @click="handleEditOrder(order)"
                          class="inline-flex items-center text-gray-600 hover:text-gray-900 bg-white hover:bg-gray-50 px-2 py-1 rounded text-xs transition-colors"
                          title="编辑订单"
                        >
                          <svg class="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                          编辑
                        </button>
                        <button
                          v-if="canDeleteOrders"
                          @click="handleDeleteOrder(order)"
                          :disabled="order.deleting"
                          class="inline-flex items-center text-red-600 hover:text-red-900 bg-red-50 hover:bg-red-100 px-2 py-1 rounded text-xs transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title="删除订单"
                        >
                          <svg v-if="!order.deleting" class="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                          <svg v-else class="animate-spin h-4 w-4 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          {{ order.deleting ? '删除中...' : '删除' }}
                        </button>
                      </div>
                    </template>
                    
                    <!-- 日期时间字段 -->
                    <template v-else-if="['created_at', 'refund_time'].includes(fieldConfig.field_name)">
                      <div class="truncate" :title="order[fieldConfig.field_name] ? formatDate(order[fieldConfig.field_name]) : '-'">
                        {{ order[fieldConfig.field_name] ? formatDate(order[fieldConfig.field_name]) : '-' }}
                      </div>
                    </template>
                    
                    <!-- 日期字段（精确到秒） -->
                    <template v-else-if="['order_date', 'feedback_date'].includes(fieldConfig.field_name)">
                      <div class="truncate" :title="order[fieldConfig.field_name] ? formatDate(order[fieldConfig.field_name]) : '-'">
                        {{ order[fieldConfig.field_name] ? formatDate(order[fieldConfig.field_name]) : '-' }}
                      </div>
                    </template>
                    
                    <!-- 普通字段 -->
                    <template v-else>
                      <div class="truncate" :title="order[fieldConfig.field_name] || '-'">
                        {{ order[fieldConfig.field_name] || '-' }}
                      </div>
                    </template>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- 右侧固定列：操作（始终显示） -->
          <div 
            class="flex-shrink-0 bg-gray-50"
            :style="{ width: `${actionColumnWidth}px` }"
            ref="rightTableContainer"
          >
            <table class="w-full">
              <thead class="bg-gray-100">
                <tr>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider h-[52px]">
                    {{ actionFieldConfig?.field_label || '操作' }}
                  </th>
                </tr>
              </thead>
              <tbody class="bg-gray-50 divide-y divide-gray-200">
                <tr v-for="order in orders" :key="`action-${order.id}`" class="hover:bg-gray-100 group">
                  <td class="px-6 py-4 text-sm text-gray-500 group-hover:bg-gray-100 transition-colors">
                    <div class="flex items-center space-x-3">
                      <button
                        @click="handleEditOrder(order)"
                        class="inline-flex items-center text-gray-600 hover:text-gray-900 bg-white hover:bg-gray-50 px-2 py-1 rounded text-xs transition-colors"
                        title="编辑订单"
                      >
                        <svg class="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        编辑
                      </button>
                      <button
                        v-if="canDeleteOrders"
                        @click="handleDeleteOrder(order)"
                        :disabled="order.deleting"
                        class="inline-flex items-center text-red-600 hover:text-red-900 bg-red-50 hover:bg-red-100 px-2 py-1 rounded text-xs transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title="删除订单"
                      >
                        <svg v-if="!order.deleting" class="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        <svg v-else class="animate-spin h-4 w-4 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 818-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        {{ order.deleting ? '删除中...' : '删除' }}
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

        <!-- 移动端卡片视图 -->
        <div v-if="!loading && orders.length > 0" class="block sm:hidden space-y-4 p-4">
          <!-- 全选功能 -->
          <div class="flex items-center justify-between bg-gray-50 px-4 py-3 rounded-lg">
            <div class="flex items-center space-x-3">
              <input
                type="checkbox"
                :checked="isAllSelected"
                :indeterminate="isIndeterminate"
                @change="toggleSelectAll"
                class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span class="text-sm text-gray-700">全选</span>
            </div>
            <span class="text-sm text-gray-500">
              {{ selectedOrders.length > 0 ? `已选择 ${selectedOrders.length} 个` : `共 ${orders.length} 个订单` }}
            </span>
          </div>

          <!-- 订单卡片列表 -->
          <div
            v-for="order in orders"
            :key="order.id"
            class="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow border border-gray-200"
          >
            <!-- 卡片头部 -->
            <div class="p-4 border-b border-gray-100">
              <div class="flex items-start justify-between">
                <div class="flex items-start space-x-3 flex-1">
                  <input
                    type="checkbox"
                    :value="order.id"
                    v-model="selectedOrders"
                    class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-1"
                  />
                  <div class="flex-1 min-w-0">
                    <h3 class="text-base font-medium text-gray-900 truncate">
                      {{ order.order_number }}
                    </h3>
                    <p class="text-sm text-gray-500">{{ order.service_provider }}</p>
                    <div class="flex flex-wrap gap-2 mt-2">
                      <span :class="getStatusClass(order.status)" class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium">
                        {{ order.status_display }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- 卡片内容 -->
            <div class="p-4 space-y-3">
              <div class="flex items-center space-x-2">
                <span class="text-xs font-medium text-gray-500">产品型号:</span>
                <span class="text-sm text-gray-900">{{ order.product_model }}</span>
              </div>
              <div class="flex items-center space-x-2">
                <span class="text-xs font-medium text-gray-500">返款金额:</span>
                <span class="text-sm font-medium text-green-600">${{ formatAmount(order.paypal_refund_amount) }}</span>
              </div>
              <div class="flex items-center space-x-2">
                <span class="text-xs font-medium text-gray-500">佣金:</span>
                <span class="text-sm font-medium text-purple-600">¥{{ formatAmount(order.commission) }}</span>
              </div>
            </div>

            <!-- 卡片底部操作 -->
            <div class="px-4 py-3 bg-gray-50 border-t border-gray-100 rounded-b-lg">
              <div class="flex items-center justify-between space-x-2">
                <button
                  @click="handleEditOrder(order)"
                  class="inline-flex items-center text-gray-600 hover:text-gray-900 bg-white hover:bg-gray-50 px-3 py-2 rounded-md text-xs font-medium transition-colors border border-gray-200"
                >
                  <svg class="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  编辑
                </button>
                <button
                  v-if="canDeleteOrders"
                  @click="handleDeleteOrder(order)"
                  :disabled="order.deleting"
                  class="inline-flex items-center text-red-600 hover:text-red-900 bg-red-50 hover:bg-red-100 px-3 py-2 rounded-md text-xs font-medium transition-colors border border-red-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg v-if="!order.deleting" class="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  {{ order.deleting ? '删除中...' : '删除' }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- 分页 -->
        <div v-if="orders.length > 0" class="bg-white border-t border-gray-200">
          <div class="flex items-center justify-center sm:justify-between px-6 py-3">
            <div class="hidden sm:flex items-center space-x-4">
              <span class="text-sm text-gray-700">
                显示第 {{ (currentPage - 1) * pageSize + 1 }} - {{ Math.min(currentPage * pageSize, totalOrders) }} 条，
                共 {{ totalOrders }} 条记录
              </span>
              <div class="flex items-center space-x-2">
                <span class="text-sm text-gray-700">每页</span>
                <select
                  v-model="pageSize"
                  @change="handlePageSizeChange"
                  class="border border-gray-300 rounded-md px-3 py-1 pr-8 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[60px] w-auto"
                >
                  <option value="5">5</option>
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
                <span class="text-sm text-gray-700">条</span>
              </div>
            </div>
            <div class="flex items-center space-x-2">
              <button
                @click="prevPage"
                :disabled="currentPage === 1 || loading"
                class="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                </svg>
                上一页
              </button>
              <span class="px-3 py-2 text-sm font-medium text-gray-700">
                第 {{ currentPage }} / {{ totalPages }} 页
              </span>
              <button
                @click="nextPage"
                :disabled="currentPage === totalPages || loading"
                class="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                下一页
                <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 订单编辑/添加模态框 -->
    <ExternalOrderModal
      :show="showOrderModal"
      :order="editingOrder"
      @close="closeOrderModal"
      @success="handleOrderModalSuccess"
    />

    <!-- 字段配置模态框 -->
    <ExternalOrderFieldConfigModal
      :show="showFieldConfigModal"
      @close="closeFieldConfigModal"
      @success="handleFieldConfigSuccess"
    />

    <!-- 导出模态框 -->
    <ExternalOrderExportModal
      :show="showExportModal"
      :total-count="totalOrders"
      :filtered-count="filteredCount"
      :selected-orders="selectedOrders"
      :has-filters="hasFilters"
      @close="closeExportModal"
      @export="handleConfirmExport"
    />

    <!-- 导入模态框 -->
    <ExternalOrderImportModal
      v-if="showImportModal"
      :show="showImportModal"
      @close="closeImportModal"
      @imported="handleImportCompleted"
    />

    <!-- 图片预览模态框 -->
    <div v-if="showImageModal" class="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black bg-opacity-75" @click="showImageModal = false">
      <div class="relative max-w-7xl max-h-full">
        <button
          @click="showImageModal = false"
          class="absolute top-4 right-4 text-white hover:text-gray-300 bg-black bg-opacity-50 rounded-full p-2"
        >
          <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        <img :src="previewImageUrl" alt="预览" class="max-w-full max-h-[90vh] object-contain" @click.stop>
      </div>
    </div>

    <!-- 时间筛选模态框 -->
    <Teleport to="body">
      <div
        v-if="showDateFilterModal"
        class="fixed inset-0 z-50 flex items-center justify-center p-4"
        @click.self="showDateFilterModal = false"
      >
        <!-- 背景遮罩 -->
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" @click="showDateFilterModal = false"></div>

        <!-- 模态框内容 -->
        <div class="relative bg-white rounded-lg shadow-xl max-w-md w-full">
          <!-- 头部 -->
          <div class="flex items-center justify-between px-5 py-4 border-b border-gray-200">
            <div class="flex items-center">
              <svg class="h-5 w-5 text-blue-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <h3 class="text-base font-semibold text-gray-900">时间筛选</h3>
            </div>
            <button
              @click="showDateFilterModal = false"
              class="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <!-- 内容 -->
          <div class="px-5 py-5 space-y-4">
            <!-- 日期字段选择 -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">筛选字段</label>
              <select
                v-model="tempDateField"
                class="w-full h-10 px-3 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:bg-white transition-all duration-200"
              >
                <option value="">全部时间</option>
                <option value="order_date">出单日期</option>
                <option value="feedback_date">反馈日期</option>
                <option value="refund_time">返款时间</option>
                <option value="created_at">创建时间</option>
              </select>
            </div>

            <!-- 开始日期 -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">开始日期</label>
              <input
                v-model="tempStartDate"
                type="date"
                :disabled="!tempDateField"
                class="w-full h-10 px-3 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:bg-white transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </div>

            <!-- 结束日期 -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">结束日期</label>
              <input
                v-model="tempEndDate"
                type="date"
                :disabled="!tempDateField"
                class="w-full h-10 px-3 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:bg-white transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </div>
          </div>

          <!-- 底部按钮 -->
          <div class="flex justify-between px-5 py-4 bg-gray-50 border-t border-gray-200 gap-3 rounded-b-lg">
            <button
              @click="clearDateFilterInModal"
              class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 focus:ring-2 focus:ring-blue-500/20 transition-all duration-200"
            >
              清除
            </button>
            <div class="flex gap-2">
              <button
                @click="showDateFilterModal = false"
                class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 focus:ring-2 focus:ring-blue-500/20 transition-all duration-200"
              >
                取消
              </button>
              <button
                @click="applyDateFilter"
                class="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg hover:from-blue-700 hover:to-blue-800 focus:ring-2 focus:ring-blue-500/20 transition-all duration-200"
              >
                确定
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- 财务统计模态框 - 全屏 -->
    <div v-if="showFinancialSummaryModal" class="fixed inset-0 z-50 bg-white flex flex-col">
      <!-- 标题 -->
      <div class="flex items-start justify-between p-6 border-b border-gray-200 flex-shrink-0">
        <div class="flex-1 mr-4">
          <h3 class="text-xl font-semibold text-gray-900">财务统计汇总</h3>
          <div class="flex items-center gap-2 mt-1">
            <p class="text-sm text-gray-500">当前筛选条件下的订单财务数据统计</p>
            <span class="text-xs font-medium text-blue-600 bg-blue-50 px-2 py-1 rounded whitespace-nowrap">{{ activeFiltersDescription }}</span>
          </div>
        </div>
        <button
          @click="showFinancialSummaryModal = false"
          class="text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-2 rounded-full transition-all flex-shrink-0"
          title="关闭"
        >
          <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- 主体内容 - 可滚动区域 -->
      <div class="flex-1 overflow-y-auto min-h-0 p-6">
        <div v-if="financialSummary" class="space-y-6 max-w-7xl mx-auto">
          <!-- 总体汇总 -->
          <div>
            <h4 class="text-base font-semibold text-gray-900 pb-2 border-b border-gray-200 mb-4">总体汇总</h4>
            <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div class="bg-white border border-gray-200 rounded-lg p-4">
                <div class="text-sm text-gray-600 mb-1">订单总数</div>
                <div class="text-2xl font-bold text-gray-900">{{ financialSummary.total_count || 0 }}</div>
              </div>
              <div class="bg-white border border-gray-200 rounded-lg p-4">
                <div class="text-sm text-gray-600 mb-1">PayPal返款</div>
                <div class="text-2xl font-bold text-green-600">${{ (financialSummary.total_paypal_refund || 0).toFixed(2) }}</div>
              </div>
              <div class="bg-white border border-gray-200 rounded-lg p-4">
                <div class="text-sm text-gray-600 mb-1">本金</div>
                <div class="text-2xl font-bold text-blue-600">${{ (financialSummary.total_principal || 0).toFixed(2) }}</div>
              </div>
              <div class="bg-white border border-gray-200 rounded-lg p-4">
                <div class="text-sm text-gray-600 mb-1">本金＋PP</div>
                <div class="text-2xl font-bold text-blue-600">${{ (financialSummary.total_principal_plus_pp || 0).toFixed(2) }}</div>
              </div>
              <div class="bg-white border border-gray-200 rounded-lg p-4">
                <div class="text-sm text-gray-600 mb-1">佣金</div>
                <div class="text-2xl font-bold text-purple-600">¥{{ (financialSummary.total_commission || 0).toFixed(2) }}</div>
              </div>
              <div class="bg-white border border-gray-200 rounded-lg p-4">
                <div class="text-sm text-gray-600 mb-1">其他金额</div>
                <div class="text-2xl font-bold text-gray-600">¥{{ (financialSummary.total_other_amount || 0).toFixed(2) }}</div>
              </div>
              <div class="bg-white border border-gray-200 rounded-lg p-4 sm:col-span-2">
                <div class="text-sm text-gray-600 mb-1">人民币汇总</div>
                <div class="text-2xl font-bold text-orange-600">¥{{ (financialSummary.total_rmb || 0).toFixed(2) }}</div>
              </div>
            </div>
          </div>

          <!-- 汇率统计 -->
          <div>
            <h4 class="text-base font-semibold text-gray-900 pb-2 border-b border-gray-200 mb-4">汇率统计</h4>
            <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div class="bg-white border border-gray-200 rounded-lg p-4">
                <div class="text-sm text-gray-600 mb-1">最大汇率</div>
                <div class="text-xl font-bold text-red-600">{{ (financialSummary.max_exchange_rate || 0).toFixed(4) }}</div>
              </div>
              <div class="bg-white border border-gray-200 rounded-lg p-4">
                <div class="text-sm text-gray-600 mb-1">最小汇率</div>
                <div class="text-xl font-bold text-green-600">{{ (financialSummary.min_exchange_rate || 0).toFixed(4) }}</div>
              </div>
              <div class="bg-white border border-gray-200 rounded-lg p-4">
                <div class="text-sm text-gray-600 mb-1">平均汇率</div>
                <div class="text-xl font-bold text-blue-600">{{ (financialSummary.avg_exchange_rate || 0).toFixed(4) }}</div>
              </div>
            </div>
          </div>

          <!-- 汇损统计 -->
          <div>
            <h4 class="text-base font-semibold text-gray-900 pb-2 border-b border-gray-200 mb-4">汇损统计</h4>
            <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div class="bg-white border border-gray-200 rounded-lg p-4">
                <div class="text-sm text-gray-600 mb-1">最大汇损</div>
                <div class="text-xl font-bold text-red-600">{{ (financialSummary.max_exchange_loss || 0).toFixed(4) }}</div>
              </div>
              <div class="bg-white border border-gray-200 rounded-lg p-4">
                <div class="text-sm text-gray-600 mb-1">最小汇损</div>
                <div class="text-xl font-bold text-green-600">{{ (financialSummary.min_exchange_loss || 0).toFixed(4) }}</div>
              </div>
              <div class="bg-white border border-gray-200 rounded-lg p-4">
                <div class="text-sm text-gray-600 mb-1">平均汇损</div>
                <div class="text-xl font-bold text-blue-600">{{ (financialSummary.avg_exchange_loss || 0).toFixed(4) }}</div>
              </div>
            </div>
          </div>

          <!-- 分组统计（服务商/商品） -->
          <div v-if="(financialSummary.service_provider_summary && financialSummary.service_provider_summary.length > 0) || (financialSummary.product_summary && financialSummary.product_summary.length > 0)">
            <h4 class="text-base font-semibold text-gray-900 pb-2 border-b border-gray-200 mb-4">分组统计</h4>
            
            <!-- Tab栏 -->
            <div class="mb-4">
              <div class="border-b border-gray-200">
                <nav class="-mb-px flex space-x-8">
                  <button
                    @click="activeStatsTab = 'service_provider'"
                    :class="[
                      'py-2 px-1 border-b-2 font-medium text-sm',
                      activeStatsTab === 'service_provider'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    ]"
                  >
                    服务商统计
                    <span v-if="financialSummary.service_provider_summary" class="ml-2 bg-gray-100 text-gray-900 py-0.5 px-2.5 rounded-full text-xs font-medium">
                      {{ financialSummary.service_provider_summary.length }}
                    </span>
                  </button>
                  <button
                    @click="activeStatsTab = 'product'"
                    :class="[
                      'py-2 px-1 border-b-2 font-medium text-sm',
                      activeStatsTab === 'product'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    ]"
                  >
                    商品统计
                    <span v-if="financialSummary.product_summary" class="ml-2 bg-gray-100 text-gray-900 py-0.5 px-2.5 rounded-full text-xs font-medium">
                      {{ financialSummary.product_summary.length }}
                    </span>
                  </button>
                </nav>
              </div>
            </div>

            <!-- 服务商统计表格 -->
            <div v-if="activeStatsTab === 'service_provider' && financialSummary.service_provider_summary && financialSummary.service_provider_summary.length > 0" class="bg-white border border-gray-200 rounded-lg overflow-x-auto">
              <table class="min-w-full divide-y divide-gray-200">
                <thead class="bg-gray-50">
                  <tr>
                    <th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-0 bg-gray-50 z-10">服务商</th>
                    <th class="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">订单数</th>
                    <th class="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">PayPal返款</th>
                    <th class="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">本金</th>
                    <th class="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">本金+PP</th>
                    <th class="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">佣金</th>
                    <th class="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">其他金额</th>
                    <th class="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">人民币汇总</th>
                    <th class="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">最大汇率</th>
                    <th class="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">最小汇率</th>
                    <th class="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">平均汇率</th>
                    <th class="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">最大汇损</th>
                    <th class="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">最小汇损</th>
                    <th class="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">平均汇损</th>
                  </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                  <tr v-for="(provider, index) in financialSummary.service_provider_summary" :key="index" class="hover:bg-gray-50">
                    <td class="px-3 py-3 text-sm font-medium text-gray-900 sticky left-0 bg-white z-10 whitespace-nowrap">{{ provider.service_provider }}</td>
                    <td class="px-3 py-3 text-sm text-gray-900 text-right whitespace-nowrap">{{ provider.count }}</td>
                    <td class="px-3 py-3 text-sm text-green-600 text-right font-medium whitespace-nowrap">${{ provider.total_paypal_refund.toFixed(2) }}</td>
                    <td class="px-3 py-3 text-sm text-blue-600 text-right font-medium whitespace-nowrap">${{ provider.total_principal.toFixed(2) }}</td>
                    <td class="px-3 py-3 text-sm text-blue-600 text-right font-medium whitespace-nowrap">${{ provider.total_principal_plus_pp.toFixed(2) }}</td>
                    <td class="px-3 py-3 text-sm text-purple-600 text-right font-medium whitespace-nowrap">¥{{ provider.total_commission.toFixed(2) }}</td>
                    <td class="px-3 py-3 text-sm text-gray-600 text-right font-medium whitespace-nowrap">¥{{ provider.total_other_amount.toFixed(2) }}</td>
                    <td class="px-3 py-3 text-sm text-orange-600 text-right font-medium whitespace-nowrap">¥{{ provider.total_rmb.toFixed(2) }}</td>
                    <td class="px-3 py-3 text-sm text-red-600 text-right whitespace-nowrap">{{ provider.max_exchange_rate.toFixed(4) }}</td>
                    <td class="px-3 py-3 text-sm text-green-600 text-right whitespace-nowrap">{{ provider.min_exchange_rate.toFixed(4) }}</td>
                    <td class="px-3 py-3 text-sm text-blue-600 text-right whitespace-nowrap">{{ provider.avg_exchange_rate.toFixed(4) }}</td>
                    <td class="px-3 py-3 text-sm text-red-600 text-right whitespace-nowrap">{{ provider.max_exchange_loss.toFixed(4) }}</td>
                    <td class="px-3 py-3 text-sm text-green-600 text-right whitespace-nowrap">{{ provider.min_exchange_loss.toFixed(4) }}</td>
                    <td class="px-3 py-3 text-sm text-blue-600 text-right whitespace-nowrap">{{ provider.avg_exchange_loss.toFixed(4) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- 商品统计表格 -->
            <div v-if="activeStatsTab === 'product' && financialSummary.product_summary && financialSummary.product_summary.length > 0" class="bg-white border border-gray-200 rounded-lg overflow-x-auto">
              <table class="min-w-full divide-y divide-gray-200">
                <thead class="bg-gray-50">
                  <tr>
                    <th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-0 bg-gray-50 z-10">商品型号</th>
                    <th class="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">订单数</th>
                    <th class="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">PayPal返款</th>
                    <th class="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">本金</th>
                    <th class="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">本金+PP</th>
                    <th class="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">佣金</th>
                    <th class="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">其他金额</th>
                    <th class="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">人民币汇总</th>
                    <th class="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">最大汇率</th>
                    <th class="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">最小汇率</th>
                    <th class="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">平均汇率</th>
                    <th class="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">最大汇损</th>
                    <th class="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">最小汇损</th>
                    <th class="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">平均汇损</th>
                  </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                  <tr v-for="(product, index) in financialSummary.product_summary" :key="index" class="hover:bg-gray-50">
                    <td class="px-3 py-3 text-sm font-medium text-gray-900 sticky left-0 bg-white z-10 whitespace-nowrap">{{ product.product_model }}</td>
                    <td class="px-3 py-3 text-sm text-gray-900 text-right whitespace-nowrap">{{ product.count }}</td>
                    <td class="px-3 py-3 text-sm text-green-600 text-right font-medium whitespace-nowrap">${{ product.total_paypal_refund.toFixed(2) }}</td>
                    <td class="px-3 py-3 text-sm text-blue-600 text-right font-medium whitespace-nowrap">${{ product.total_principal.toFixed(2) }}</td>
                    <td class="px-3 py-3 text-sm text-blue-600 text-right font-medium whitespace-nowrap">${{ product.total_principal_plus_pp.toFixed(2) }}</td>
                    <td class="px-3 py-3 text-sm text-purple-600 text-right font-medium whitespace-nowrap">¥{{ product.total_commission.toFixed(2) }}</td>
                    <td class="px-3 py-3 text-sm text-gray-600 text-right font-medium whitespace-nowrap">¥{{ product.total_other_amount.toFixed(2) }}</td>
                    <td class="px-3 py-3 text-sm text-orange-600 text-right font-medium whitespace-nowrap">¥{{ product.total_rmb.toFixed(2) }}</td>
                    <td class="px-3 py-3 text-sm text-red-600 text-right whitespace-nowrap">{{ product.max_exchange_rate.toFixed(4) }}</td>
                    <td class="px-3 py-3 text-sm text-green-600 text-right whitespace-nowrap">{{ product.min_exchange_rate.toFixed(4) }}</td>
                    <td class="px-3 py-3 text-sm text-blue-600 text-right whitespace-nowrap">{{ product.avg_exchange_rate.toFixed(4) }}</td>
                    <td class="px-3 py-3 text-sm text-red-600 text-right whitespace-nowrap">{{ product.max_exchange_loss.toFixed(4) }}</td>
                    <td class="px-3 py-3 text-sm text-green-600 text-right whitespace-nowrap">{{ product.min_exchange_loss.toFixed(4) }}</td>
                    <td class="px-3 py-3 text-sm text-blue-600 text-right whitespace-nowrap">{{ product.avg_exchange_loss.toFixed(4) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- 空状态 -->
            <div v-if="activeStatsTab === 'service_provider' && (!financialSummary.service_provider_summary || financialSummary.service_provider_summary.length === 0)" class="text-center py-8 text-gray-500">
              <p>暂无服务商统计数据</p>
            </div>
            <div v-if="activeStatsTab === 'product' && (!financialSummary.product_summary || financialSummary.product_summary.length === 0)" class="text-center py-8 text-gray-500">
              <p>暂无商品统计数据</p>
            </div>
          </div>

        </div>

        <div v-else class="flex items-center justify-center h-full">
          <div class="text-center text-gray-500">
            <svg class="h-12 w-12 mx-auto mb-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p>暂无财务数据</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick, watch } from 'vue'
import { useToast } from 'vue-toastification'
import { useAuthStore } from '@/stores/auth'
import orderService from '@/services/orderService'
import SearchableSelect from '@/components/common/SearchableSelect.vue'
import ExternalOrderModal from '@/components/orders/ExternalOrderModal.vue'
import ExternalOrderFieldConfigModal from '@/components/orders/ExternalOrderFieldConfigModal.vue'
import ExternalOrderExportModal from '@/components/orders/ExternalOrderExportModal.vue'
import ExternalOrderImportModal from '@/components/orders/ExternalOrderImportModal.vue'

const toast = useToast()
const authStore = useAuthStore()

// 响应式数据
const searchQuery = ref('')
const selectedServiceProvider = ref('')
const selectedRefundType = ref('')
const selectedDateField = ref('')  // 选择的日期字段
const startDate = ref('')  // 开始日期
const endDate = ref('')  // 结束日期
const financialSummary = ref(null)  // 财务汇总信息
const showFinancialSummaryModal = ref(false)  // 财务统计模态框
const activeStatsTab = ref('service_provider')  // 当前活跃的统计tab（service_provider/product）
const showDateFilterModal = ref(false)  // 时间筛选模态框
const tempDateField = ref('')  // 临时日期字段
const tempStartDate = ref('')  // 临时开始日期
const tempEndDate = ref('')  // 临时结束日期
const currentPage = ref(1)
const pageSize = ref(10)
const orders = ref([])
const loading = ref(true)
const totalOrders = ref(0)
const apiError = ref('')
const fieldConfigs = ref([])
const duplicateOrderNumbers = ref([])
const statistics = ref({
  total_count: 0,
  total_refund: 0,
  total_commission: 0,
  total_exchange_loss: 0,
  total_rmb: 0
})
const serviceProviders = ref([])

// 服务商选项（转换为 SearchableSelect 需要的格式，过滤空值）
const serviceProviderOptions = computed(() => {
  const filteredProviders = serviceProviders.value
    .filter(provider => provider && provider.trim() !== '') // 过滤空字符串
    .map(provider => ({
      id: provider,
      name: provider
    }))
  
  // 在开头添加"全部服务商"选项
  return [
    { id: '', name: '全部服务商' },
    ...filteredProviders
  ]
})

// 返款形式选项
const refundTypeOptions = [
  { id: '', name: '全部返款形式' },
  { id: 'pp_direct', name: 'PP美金直返' },
  { id: 'principal_direct', name: '本金直返人民币' },
  { id: 'principal_loss', name: '本金加汇损直返人民币' },
  { id: 'principal_pp', name: '本金加pp手续费直返人民币' }
]

// 批量操作
const selectedOrders = ref([])
const bulkDeleting = ref(false)

// 模态框状态
const showOrderModal = ref(false)
const editingOrder = ref(null)
const showFieldConfigModal = ref(false)
const showExportModal = ref(false)
const showImportModal = ref(false)
const showImageModal = ref(false)
const previewImageUrl = ref('')

// 表格容器引用
const tableContainer = ref(null)
const leftTableContainer = ref(null)
const middleTableContainer = ref(null)
const rightTableContainer = ref(null)

// 权限检查
const isSubAccount = computed(() => {
  return authStore.user?.account_type === 'sub'
})

const canDeleteOrders = computed(() => {
  if (!isSubAccount.value) {
    return true
  }
  return authStore.user?.sub_permissions?.can_delete_orders === true
})

const canExportOrders = computed(() => {
  if (!isSubAccount.value) {
    return true
  }
  return authStore.user?.sub_permissions?.can_export_orders === true
})

// 计算属性
const totalPages = computed(() => Math.ceil(totalOrders.value / pageSize.value))

const isAllSelected = computed(() => {
  return orders.value.length > 0 && selectedOrders.value.length === orders.value.length
})

const isIndeterminate = computed(() => {
  return selectedOrders.value.length > 0 && selectedOrders.value.length < orders.value.length
})

// 导出相关计算属性
const hasFilters = computed(() => {
  return !!(searchQuery.value || selectedServiceProvider.value || selectedRefundType.value || selectedDateField.value)
})

// 获取当前筛选条件的描述
const activeFiltersDescription = computed(() => {
  const filters = []
  
  // 搜索关键词
  if (searchQuery.value && searchQuery.value.trim()) {
    filters.push(`搜索关键词: "${searchQuery.value.trim()}"`)
  }
  
  // 服务商
  if (selectedServiceProvider.value) {
    filters.push(`服务商: ${selectedServiceProvider.value}`)
  }
  
  // 返款形式
  if (selectedRefundType.value) {
    const refundTypeLabel = refundTypeOptions.find(opt => opt.id === selectedRefundType.value)?.name || selectedRefundType.value
    filters.push(`返款形式: ${refundTypeLabel.replace('全部返款形式', '')}`)
  }
  
  // 时间筛选
  if (selectedDateField.value) {
    const dateFieldLabels = {
      'order_date': '出单日期',
      'feedback_date': '反馈日期',
      'refund_time': '返款时间',
      'created_at': '创建时间'
    }
    const fieldLabel = dateFieldLabels[selectedDateField.value] || selectedDateField.value
    
    let dateRange = ''
    if (startDate.value && endDate.value) {
      dateRange = `${startDate.value} 至 ${endDate.value}`
    } else if (startDate.value) {
      dateRange = `${startDate.value} 起`
    } else if (endDate.value) {
      dateRange = `至 ${endDate.value}`
    }
    
    if (dateRange) {
      filters.push(`${fieldLabel}: ${dateRange}`)
    }
  }
  
  return filters.length > 0 ? filters.join(' | ') : '全部订单'
})

// 清除日期筛选
const clearDateFilter = () => {
  selectedDateField.value = ''
  startDate.value = ''
  endDate.value = ''
  handleSearch()
}

// 应用日期筛选
const applyDateFilter = () => {
  selectedDateField.value = tempDateField.value
  startDate.value = tempStartDate.value
  endDate.value = tempEndDate.value
  showDateFilterModal.value = false
  handleSearch()
}

// 在模态框中清除日期筛选
const clearDateFilterInModal = () => {
  tempDateField.value = ''
  tempStartDate.value = ''
  tempEndDate.value = ''
}

// 监听模态框打开，复制当前值到临时状态
watch(showDateFilterModal, (newVal) => {
  if (newVal) {
    tempDateField.value = selectedDateField.value
    tempStartDate.value = startDate.value
    tempEndDate.value = endDate.value
  }
})

const filteredCount = computed(() => {
  return totalOrders.value
})

const visibleFieldConfigs = computed(() => {
  if (!Array.isArray(fieldConfigs.value)) {
    return []
  }
  return fieldConfigs.value
    .filter(config => config.is_visible && config.field_name !== 'actions')
    .sort((a, b) => a.sort_order - b.sort_order)
})

const actionFieldConfig = computed(() => {
  if (!Array.isArray(fieldConfigs.value)) {
    return null
  }
  return fieldConfigs.value.find(config => config.field_name === 'actions')
})

// 动态计算操作列宽度
const actionColumnWidth = computed(() => {
  // 如果有删除权限，使用默认宽度或配置宽度
  if (canDeleteOrders.value) {
    return actionFieldConfig.value?.column_width || 160
  }
  // 如果没有删除权限，只有编辑按钮，使用更小的宽度
  return 120
})

// 批量操作方法
const toggleSelectAll = () => {
  if (isAllSelected.value) {
    selectedOrders.value = []
  } else {
    selectedOrders.value = orders.value.map(order => order.id)
  }
}

// 同步表格行高度
const syncTableRowHeights = async () => {
  await nextTick()
  
  if (!leftTableContainer.value || !middleTableContainer.value || !rightTableContainer.value) {
    return
  }
  
  try {
    const leftRows = leftTableContainer.value.querySelectorAll('tbody tr')
    const middleRows = middleTableContainer.value.querySelectorAll('tbody tr')
    const rightRows = rightTableContainer.value.querySelectorAll('tbody tr')
    
    if (leftRows.length !== middleRows.length || middleRows.length !== rightRows.length) {
      return
    }
    
    // 重置所有行的高度为auto
    leftRows.forEach(row => row.style.height = 'auto')
    middleRows.forEach(row => row.style.height = 'auto')
    rightRows.forEach(row => row.style.height = 'auto')
    
    // 等待一帧以确保高度计算正确
    await new Promise(resolve => requestAnimationFrame(resolve))
    
    // 计算每一行的最大高度并应用
    for (let i = 0; i < middleRows.length; i++) {
      const leftHeight = leftRows[i]?.offsetHeight || 0
      const middleHeight = middleRows[i]?.offsetHeight || 0
      const rightHeight = rightRows[i]?.offsetHeight || 0
      
      const maxHeight = Math.max(leftHeight, middleHeight, rightHeight)
      
      if (maxHeight > 0) {
        leftRows[i].style.height = `${maxHeight}px`
        middleRows[i].style.height = `${maxHeight}px`
        rightRows[i].style.height = `${maxHeight}px`
      }
    }
  } catch (error) {
    console.warn('同步表格行高度时出错:', error)
  }
}

// 搜索订单
const searchOrders = async (resetPage = true) => {
  if (resetPage) {
    currentPage.value = 1
    selectedOrders.value = []
  }
  
  loading.value = true
  apiError.value = ''
  
  try {
    const params = {
      page: currentPage.value,
      page_size: pageSize.value
    }
    
    if (searchQuery.value) {
      params.search = searchQuery.value
    }
    
    if (selectedServiceProvider.value) {
      params.service_provider = selectedServiceProvider.value
    }
    
    if (selectedRefundType.value) {
      params.refund_type = selectedRefundType.value
    }
    
    // 添加日期筛选参数
    if (selectedDateField.value) {
      params.date_field = selectedDateField.value
      if (startDate.value) {
        params.start_date = startDate.value
      }
      if (endDate.value) {
        params.end_date = endDate.value
      }
    }
    
    const result = await orderService.getExternalOrders(params)
    
    if (result.success) {
      orders.value = result.data.results || []
      totalOrders.value = result.data.count || 0
      duplicateOrderNumbers.value = result.data.duplicate_order_numbers || []
      
      // 更新财务汇总信息
      if (result.data.financial_summary) {
        financialSummary.value = result.data.financial_summary
      } else {
        financialSummary.value = null
      }
    } else {
      throw new Error(result.message)
    }
  } catch (error) {
    console.error('搜索订单失败:', error)
    apiError.value = error.message || '加载订单列表失败，请稍后重试'
    orders.value = []
    totalOrders.value = 0
  } finally {
    loading.value = false
  }
}

// 加载统计数据
const loadStatistics = async () => {
  try {
    const result = await orderService.getExternalOrderStatistics()
    if (result.success) {
      statistics.value = result.data
    }
  } catch (error) {
    console.error('加载统计数据失败:', error)
  }
}

// 加载服务商列表
const loadServiceProviders = async () => {
  try {
    const result = await orderService.getServiceProviders()
    if (result.success) {
      serviceProviders.value = result.data
    }
  } catch (error) {
    console.error('加载服务商列表失败:', error)
  }
}

// 加载字段配置
const loadFieldConfigs = async () => {
  try {
    const response = await orderService.getFieldConfigs()
    if (response.success && Array.isArray(response.data) && response.data.length > 0) {
      fieldConfigs.value = response.data
    } else {
      // 初始化默认字段配置
      await orderService.initializeFieldConfigs()
      const retryResponse = await orderService.getFieldConfigs()
      if (retryResponse.success && Array.isArray(retryResponse.data)) {
        fieldConfigs.value = retryResponse.data
      } else {
        // 确保至少是空数组
        fieldConfigs.value = []
      }
    }
  } catch (error) {
    console.error('加载字段配置失败:', error)
    fieldConfigs.value = []
  }
}

const handleSearch = () => {
  window.scrollTo({ top: 0, behavior: 'instant' })
  searchOrders(true)
}

const prevPage = async () => {
  if (currentPage.value > 1) {
    window.scrollTo({ top: 0, behavior: 'instant' })
    currentPage.value--
    selectedOrders.value = []
    await searchOrders(false)
  }
}

const nextPage = async () => {
  if (currentPage.value < totalPages.value) {
    window.scrollTo({ top: 0, behavior: 'instant' })
    currentPage.value++
    selectedOrders.value = []
    await searchOrders(false)
  }
}

const handlePageSizeChange = async () => {
  pageSize.value = Number(pageSize.value)
  window.scrollTo({ top: 0, behavior: 'instant' })
  currentPage.value = 1
  selectedOrders.value = []
  await searchOrders(false)
}

const handleAddOrder = () => {
  editingOrder.value = null
  showOrderModal.value = true
}

const handleEditOrder = (order) => {
  editingOrder.value = order
  showOrderModal.value = true
}

const closeOrderModal = () => {
  showOrderModal.value = false
}

const handleOrderModalSuccess = () => {
  searchOrders(false)
  loadStatistics()
}

const handleDeleteOrder = async (order) => {
  if (confirm(`⚠️ 确定要删除订单 ${order.order_number} 吗？\n\n删除后将从数据库中彻底移除，无法恢复！`)) {
    order.deleting = true
    
    try {
      const result = await orderService.deleteExternalOrder(order.id)
      
      if (result.success) {
        toast.success(result.message || '订单已删除')
        await searchOrders(false)
        await loadStatistics()
        
        if (orders.value.length === 0 && currentPage.value > 1) {
          currentPage.value--
          await searchOrders(false)
        }
      } else {
        toast.error(result.message || '删除订单失败')
      }
    } catch (error) {
      console.error('删除订单失败:', error)
      toast.error('删除订单失败')
    } finally {
      order.deleting = false
    }
  }
}

const handleBulkDelete = async () => {
  if (selectedOrders.value.length === 0) {
    toast.warning('请选择要删除的订单')
    return
  }

  const count = selectedOrders.value.length
  if (confirm(`⚠️ 确定要永久删除选中的 ${count} 个订单吗？\n\n删除后将从数据库中彻底移除，无法恢复！`)) {
    bulkDeleting.value = true
    
    try {
      const result = await orderService.bulkDeleteExternalOrders(selectedOrders.value)
      
      if (result.success) {
        toast.success(result.message || `成功删除 ${count} 个订单`)
        selectedOrders.value = []
        await searchOrders(false)
        await loadStatistics()
        
        if (orders.value.length === 0 && currentPage.value > 1) {
          currentPage.value--
          await searchOrders(false)
        }
      } else {
        toast.error(result.message || '批量删除失败')
      }
    } catch (error) {
      console.error('批量删除失败:', error)
      toast.error('批量删除失败，请稍后重试')
    } finally {
      bulkDeleting.value = false
    }
  }
}

const handleFieldConfig = () => {
  showFieldConfigModal.value = true
}

const closeFieldConfigModal = () => {
  showFieldConfigModal.value = false
}

const handleFieldConfigSuccess = async () => {
  await loadFieldConfigs()
  await searchOrders(false)
}

const handleExport = () => {
  showExportModal.value = true
}

const closeExportModal = () => {
  showExportModal.value = false
}

const handleConfirmExport = async (exportConfig) => {
  try {
    let orderIds = []
    let filters = {}
    
    // 根据导出范围决定参数
    if (exportConfig.range === 'selected') {
      // 导出选中的订单
      orderIds = selectedOrders.value
    } else if (exportConfig.range === 'filtered') {
      // 导出当前筛选结果，传递筛选条件
      filters = {
        search: searchQuery.value,
        service_provider: selectedServiceProvider.value,
        refund_type: selectedRefundType.value
      }
    }
    // else 导出全部订单，不传递任何参数
    
    const result = await orderService.exportExternalOrders(orderIds, exportConfig.format, filters)
    
    if (result.success) {
      const url = window.URL.createObjectURL(result.data)
      const link = document.createElement('a')
      link.href = url
      link.download = result.filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      
      toast.success('订单数据导出成功')
    } else {
      throw new Error(result.message)
    }
  } catch (error) {
    console.error('导出订单失败:', error)
    toast.error(error.message || '导出订单失败，请稍后重试')
  } finally {
    exportConfig.callback()
  }
}

// 导入订单
const handleImport = () => {
  showImportModal.value = true
}

const closeImportModal = () => {
  showImportModal.value = false
}

const handleImportCompleted = async (result) => {
  // 导入完成后重新加载订单列表和统计数据
  await searchOrders(true)
  await loadStatistics()
  toast.success(`导入完成！成功: ${result.data.summary.success_count}, 跳过: ${result.data.summary.skip_count}, 失败: ${result.data.summary.error_count}`)
}

// 图片预览
const showImagePreview = (imageUrl) => {
  previewImageUrl.value = imageUrl
  showImageModal.value = true
}

// 格式化金额
const formatAmount = (amount) => {
  if (!amount) return '0.00'
  return Number(amount).toFixed(2)
}

// 格式化日期
const formatDate = (dateString) => {
  const date = new Date(dateString)
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  }).replace(/\//g, '-')
}

// 获取状态样式
const getStatusClass = (status) => {
  const statusClasses = {
    'pending_refund': 'bg-yellow-100 text-yellow-800',
    'pending_review': 'bg-orange-100 text-orange-800',
    'completed': 'bg-green-100 text-green-800',
    'abnormal': 'bg-red-100 text-red-800'
  }
  return statusClasses[status] || 'bg-gray-100 text-gray-800'
}

// 窗口大小调整处理函数
const handleResize = () => {
  syncTableRowHeights()
}

// 监听订单数据变化，同步表格行高度
watch(orders, () => {
  syncTableRowHeights()
}, { deep: true })

// 组件挂载时加载数据
onMounted(async () => {
  // 刷新用户权限信息（确保获取最新权限）
  if (isSubAccount.value) {
    try {
      await authStore.fetchUserProfile()
    } catch (error) {
      console.error('刷新用户权限失败:', error)
    }
  }
  
  await Promise.all([
    loadFieldConfigs(),
    loadServiceProviders(),
    loadStatistics()
  ])
  await searchOrders()
  
  // 确保初始数据加载后同步高度
  await nextTick()
  syncTableRowHeights()
  
  // 添加窗口调整事件监听器
  window.addEventListener('resize', handleResize)
})

// 组件卸载时清理事件监听器
onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
})
</script>

<style scoped>
/* 美化表格容器的水平滚动条 */
.table-container::-webkit-scrollbar {
  height: 8px;
}

.table-container::-webkit-scrollbar-track {
  background: #f8fafc;
  border-radius: 4px;
  margin: 0 4px;
}

.table-container::-webkit-scrollbar-thumb {
  background: linear-gradient(90deg, #e2e8f0, #cbd5e1);
  border-radius: 4px;
  transition: all 0.3s ease;
}

.table-container::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(90deg, #cbd5e1, #94a3b8);
  transform: scaleY(1.2);
}

/* Firefox 滚动条样式 */
.table-container {
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 #f8fafc;
}
</style>
